import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { 
  X, Building2, User, Mail, Phone, Linkedin, Globe, 
  Calendar, MessageSquare, PhoneCall, Video, FileText,
  Plus, Clock, TrendingUp, Edit3, Save, Loader2
} from "lucide-react";

const ACTIVITY_TYPES = [
  { value: "call", label: "Call", icon: PhoneCall, color: "text-blue-400" },
  { value: "email", label: "Email", icon: Mail, color: "text-green-400" },
  { value: "meeting", label: "Meeting", icon: Video, color: "text-purple-400" },
  { value: "note", label: "Note", icon: FileText, color: "text-yellow-400" }
];

const LeadDetailModal = ({ isOpen, onClose, lead, userId, onLeadUpdated }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // New activity form
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [newActivity, setNewActivity] = useState({
    type: "note",
    subject: "",
    description: "",
    outcome: ""
  });
  const [addingActivity, setAddingActivity] = useState(false);

  useEffect(() => {
    if (isOpen && lead) {
      fetchActivities();
      setEditData({
        status: lead.status || "new",
        notes: lead.notes || "",
        contactName: lead.contactName || "",
        contactTitle: lead.contactTitle || "",
        email: lead.email || "",
        phone: lead.phone || ""
      });
    }
  }, [isOpen, lead]);

  const fetchActivities = async () => {
    if (!lead?.leadId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/activities/lead/${lead.leadId}`);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      // Activities endpoint might not exist yet, use empty array
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/leads/${lead.leadId}`, editData);
      toast.success("Lead updated successfully");
      setIsEditing(false);
      if (onLeadUpdated) {
        onLeadUpdated({ ...lead, ...editData });
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    } finally {
      setSaving(false);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }

    setAddingActivity(true);
    try {
      const activityData = {
        userId,
        leadId: lead.leadId,
        type: newActivity.type,
        subject: newActivity.subject,
        description: newActivity.description,
        outcome: newActivity.outcome
      };

      const response = await axios.post(`${API}/activities`, activityData);
      setActivities(prev => [response.data, ...prev]);
      setNewActivity({ type: "note", subject: "", description: "", outcome: "" });
      setShowAddActivity(false);
      toast.success("Activity logged");

      // Update lead's lastActivity
      await axios.put(`${API}/leads/${lead.leadId}`, {
        lastActivity: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error adding activity:", error);
      toast.error("Failed to log activity");
    } finally {
      setAddingActivity(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "converted": return "bg-green-500/20 text-green-400 border-green-500/50";
      case "qualified": return "bg-blue-500/20 text-blue-400 border-blue-500/50";
      case "contacted": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "lost": return "bg-red-500/20 text-red-400 border-red-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !lead) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-[#0f1419] to-[#1a1f24] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-800 shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#e91e8c]/20 to-[#3dd9c5]/20 border-b border-gray-800 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#e91e8c] to-[#3dd9c5] flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{lead.companyName}</h2>
                <p className="text-gray-400 text-sm">{lead.industry}</p>
              </div>
              <Badge className={`${getStatusColor(editData.status || lead.status)} border ml-4`}>
                {editData.status || lead.status}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="border-gray-700 text-gray-300 hover:bg-white/10"
                >
                  <Edit3 className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors p-2"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-100px)]">
          {/* Left Panel - Lead Info */}
          <div className="w-1/3 border-r border-gray-800 p-6 overflow-y-auto">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-4">
              Contact Information
            </h3>

            <div className="space-y-4">
              {/* Contact Name */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <User className="h-3 w-3" /> Contact Name
                </label>
                {isEditing ? (
                  <Input
                    value={editData.contactName}
                    onChange={(e) => setEditData(prev => ({ ...prev, contactName: e.target.value }))}
                    className="bg-white/5 border-gray-700 text-white text-sm"
                  />
                ) : (
                  <p className="text-white font-medium">{lead.contactName || "Not specified"}</p>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Title</label>
                {isEditing ? (
                  <Input
                    value={editData.contactTitle}
                    onChange={(e) => setEditData(prev => ({ ...prev, contactTitle: e.target.value }))}
                    className="bg-white/5 border-gray-700 text-white text-sm"
                  />
                ) : (
                  <p className="text-gray-300">{lead.contactTitle || "Not specified"}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <Mail className="h-3 w-3" /> Email
                </label>
                {isEditing ? (
                  <Input
                    value={editData.email}
                    onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-white/5 border-gray-700 text-white text-sm"
                  />
                ) : (
                  <a href={`mailto:${lead.email}`} className="text-[#3dd9c5] hover:underline text-sm">
                    {lead.email || "Not specified"}
                  </a>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <Phone className="h-3 w-3" /> Phone
                </label>
                {isEditing ? (
                  <Input
                    value={editData.phone}
                    onChange={(e) => setEditData(prev => ({ ...prev, phone: e.target.value }))}
                    className="bg-white/5 border-gray-700 text-white text-sm"
                  />
                ) : (
                  <p className="text-gray-300">{lead.phone || "Not specified"}</p>
                )}
              </div>

              {/* LinkedIn */}
              {lead.linkedinUrl && (
                <div>
                  <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                    <Linkedin className="h-3 w-3" /> LinkedIn
                  </label>
                  <a href={lead.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-[#3dd9c5] hover:underline text-sm truncate block">
                    View Profile →
                  </a>
                </div>
              )}

              {/* Website */}
              {lead.website && (
                <div>
                  <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                    <Globe className="h-3 w-3" /> Website
                  </label>
                  <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-[#3dd9c5] hover:underline text-sm truncate block">
                    {lead.website}
                  </a>
                </div>
              )}

              {/* Status */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Status</label>
                {isEditing ? (
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-white/5 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="qualified">Qualified</option>
                    <option value="converted">Converted</option>
                    <option value="lost">Lost</option>
                  </select>
                ) : (
                  <Badge className={`${getStatusColor(lead.status)} border`}>{lead.status}</Badge>
                )}
              </div>

              {/* Lead Score */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <TrendingUp className="h-3 w-3" /> Lead Score
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#e91e8c] to-[#3dd9c5]"
                      style={{ width: `${lead.score || 0}%` }}
                    />
                  </div>
                  <span className="text-white font-semibold text-sm">{lead.score || 0}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                  <MessageSquare className="h-3 w-3" /> Notes
                </label>
                {isEditing ? (
                  <Textarea
                    value={editData.notes}
                    onChange={(e) => setEditData(prev => ({ ...prev, notes: e.target.value }))}
                    className="bg-white/5 border-gray-700 text-white text-sm min-h-[80px]"
                    placeholder="Add notes about this lead..."
                  />
                ) : (
                  <p className="text-gray-400 text-sm">{lead.notes || "No notes"}</p>
                )}
              </div>

              {/* Metadata */}
              <div className="pt-4 border-t border-gray-800">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar className="h-3 w-3" />
                  Created: {formatDate(lead.createdAt)}
                </div>
                {lead.lastActivity && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <Clock className="h-3 w-3" />
                    Last Activity: {formatDate(lead.lastActivity)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Activity Timeline */}
          <div className="flex-1 p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">
                Activity Timeline
              </h3>
              <Button
                size="sm"
                onClick={() => setShowAddActivity(!showAddActivity)}
                className="bg-[#e91e8c] hover:bg-[#d01a7d] text-white"
              >
                <Plus className="h-4 w-4 mr-1" />
                Log Activity
              </Button>
            </div>

            {/* Add Activity Form */}
            {showAddActivity && (
              <div className="bg-white/5 border border-gray-700 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Type</label>
                    <div className="flex gap-2">
                      {ACTIVITY_TYPES.map(type => {
                        const Icon = type.icon;
                        return (
                          <button
                            key={type.value}
                            onClick={() => setNewActivity(prev => ({ ...prev, type: type.value }))}
                            className={`p-2 rounded-lg border transition-colors ${
                              newActivity.type === type.value
                                ? 'bg-[#3dd9c5]/20 border-[#3dd9c5]'
                                : 'border-gray-700 hover:border-gray-500'
                            }`}
                            title={type.label}
                          >
                            <Icon className={`h-4 w-4 ${type.color}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Subject</label>
                    <Input
                      value={newActivity.subject}
                      onChange={(e) => setNewActivity(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Brief subject..."
                      className="bg-white/5 border-gray-700 text-white text-sm"
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Description</label>
                  <Textarea
                    value={newActivity.description}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What happened?"
                    className="bg-white/5 border-gray-700 text-white text-sm min-h-[60px]"
                  />
                </div>
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Outcome (optional)</label>
                  <Input
                    value={newActivity.outcome}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, outcome: e.target.value }))}
                    placeholder="Result or next steps..."
                    className="bg-white/5 border-gray-700 text-white text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddActivity}
                    disabled={addingActivity}
                    className="bg-[#3dd9c5] hover:bg-[#35c4b5] text-white"
                  >
                    {addingActivity ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    Save Activity
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowAddActivity(false)}
                    className="border-gray-700 text-gray-300"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Activity List */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-[#3dd9c5]" />
                </div>
              ) : activities.length > 0 ? (
                <div className="space-y-4">
                  {activities.map((activity, idx) => {
                    const actType = ACTIVITY_TYPES.find(t => t.value === activity.type) || ACTIVITY_TYPES[3];
                    const Icon = actType.icon;
                    return (
                      <div key={activity.activityId || idx} className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 ${actType.color}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 bg-white/5 border border-gray-800 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-white font-medium text-sm">{activity.subject}</span>
                            <span className="text-xs text-gray-500">{formatDate(activity.createdAt)}</span>
                          </div>
                          {activity.description && (
                            <p className="text-gray-400 text-sm">{activity.description}</p>
                          )}
                          {activity.outcome && (
                            <p className="text-[#3dd9c5] text-xs mt-2">
                              Outcome: {activity.outcome}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-500">No activities logged yet</p>
                  <p className="text-gray-600 text-sm mt-1">Click "Log Activity" to add the first one</p>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
